import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useShowPostCheckoutCart } from '@givebacks/api/store';
import { useUrlSearchParams } from 'core/Hooks';
import { useFeatureValue } from 'core/Packages';
import { useNavigate } from 'react-router-dom';
import { ButtonMantine, ImageMantine } from 'ui';

import { useNavigateToAuth } from '../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { PostCheckoutRedirectPageTemplate } from '../PostCheckoutRedirectPageTemplate/PostCheckoutRedirectPageTemplate';
import { POST_CHECKOUT_IMAGE_URLS } from '../postCheckoutImageUrls';

const ShopToGiveRedirectPage = () => {
  const { handleNavigateToAuth } = useNavigateToAuth();
  const { isAuthenticated } = useAuthentication();
  const navigate = useNavigate();

  const testVariation = useFeatureValue<'A' | 'B'>('ic_non-membership_with_stg', 'A');

  const variantPropMap = useMemo(
    () =>
      new Map([
        [
          'A',
          {
            rightSection: <ImageMantine src={POST_CHECKOUT_IMAGE_URLS.earnOnline} />,
          },
        ],
        [
          'B',
          {
            rightSection: <ImageMantine src={POST_CHECKOUT_IMAGE_URLS.defaultMap} />,
          },
        ],
      ]),
    []
  );

  const { cartId, email, firstName, lastName } = useUrlSearchParams([
    'cartId',
    'email',
    'firstName',
    'lastName',
  ]);
  const { data } = useShowPostCheckoutCart({
    cartId,
    email,
  });

  const causeName = data?.cart.cause.name ?? 'your cause';

  const onPrimaryCtaClick = useCallback(() => {
    if (isAuthenticated) {
      navigate('/rewards/dining');
      return;
    }

    handleNavigateToAuth({
      page: 'sign-up',
      redirect: '/rewards/dining',
      additionalParams: {
        firstName,
        lastName,
      },
    });
  }, [isAuthenticated, handleNavigateToAuth, firstName, lastName, navigate]);

  return (
    <PostCheckoutRedirectPageTemplate
      title={`Find local merchants and restaurants supporting ${causeName}`}
      leftSection={
        <PostCheckoutRedirectPageTemplate.AppBenefits>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Discover participating local merchants and restaurants
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Find 15,000+ online merchants giving back
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Plus... many additional features that simplify supporting your organizations
          </PostCheckoutRedirectPageTemplate.AppBenefit>
        </PostCheckoutRedirectPageTemplate.AppBenefits>
      }
      rightSection={variantPropMap.get(testVariation)?.rightSection}
      cta={<ButtonMantine onClick={onPrimaryCtaClick}>Access Offers Near Me</ButtonMantine>}
    />
  );
};

export { ShopToGiveRedirectPage };
